import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { CSVLink } from 'react-csv';
import FilterPageService from '../../../../services/AdminServices/FilterPageService';
import Config from '../../../../config';
import LoadingCard from '../../LoadingCard';
import ToolBar from '../../ToolBar';
import { FilterListWrapper } from '../styles';
import Modal from '../Modal';
import Constants from '../../../../shared/constants';
import ListView from '../ListView/ListView';
import ListViewHeader from '../ListView/ListViewHeader';
import { Border } from '../../users/styles';
import { WhiteCard } from '../../../gigger/styles';
import Pagination from '../../../../components/Pagination/Pagination';
import { getFormattedCSVData } from '../../../../shared/utils';

const SystemsTab = ({ loading, handleToast }) => {
  const [pageLoading, setLoading] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [searchbar, setSearchBar] = useState(true);
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const csvExportFileds = ['name', 'type', 'added_by', 'created_at', 'status'];
  const filterPageService = new FilterPageService(Config.apiBase);
  const [sortedBy, setSortedBy] = useState('created_at');
  const [csvDownloadData, setCsvDownloadData] = useState([]);
  const [sortAscending, setSortAscending] = useState(false);
  const [meta, setMeta] = useState({
    previous: null,
    current: 0,
    next: 1,
    count: null,
    pageNumber: 1,
    pageSize: 15,
    pages: 1,
  });
  const openElement = useRef(null);
  const systemsCsv = useRef(null);

  /**
   *
   * @async
   * @function fetchSystems Fetches job title filters
   * @param {String} searchInput search text
   *
   */
  const fetchSystems = (searchInput) => {
    setLoading(true);
    const queryParams = [
      ['pageNumber', searchInput ? 1 : meta.pageNumber],
      ['pageSize', meta.pageSize],
      ['sortedBy', sortedBy],
      ['sortAscending', sortAscending],
      ['search', searchInput],
      ['type', Constants.filter.SYSTEMS],
    ];
    try {
      filterPageService.getSystemsList(queryParams).then((response) => {
        const { meta: srcMetaData = {} } = response;
        const {
          current_page: currentPage, per_page: perPage, last_page: pages, total: totalCount,
        } = srcMetaData;
        const metaData = {
          pageNumber: currentPage,
          pageSize: perPage,
          pages,
          totalCount,
        };
        setLoading(false);
        setFilterData(response.data);
        setMeta(metaData);
      });
    } catch (e) {
      setLoading(false);
    }
  };

  /**
   *
     * Calls fetchSystems function when the below values changes
     * [isDeleteModalOpen,isApproveModalOpen, csvDownloadData]
     *
     */
  useEffect(() => {
    if (isDeleteModalOpen || isApproveModalOpen) {
      openElement.current.click();
    }
  }, [isDeleteModalOpen, isApproveModalOpen]);

  /**
   *
     * Calls fetchSystems function when the below values changes
     * [loading, meta.pageNumber, showToast, sortAscending, sortedBy]
     *
     */
  useEffect(() => {
    fetchSystems();
  }, [loading, sortAscending, sortedBy]);

  /**
   *
   * Handles pagination
   * @param {Number} pageNumber page number
   *
   */
  const handleGotoPage = (pageNumber) => {
    meta.pageNumber = pageNumber;
    meta.pageSize = 15;
    setMeta(meta);
    fetchSystems();
  };

  /**
   *
   * Handles search functionality
   * @param {String} value search text
   *
   */
  const handlesearchSystems = (value) => {
    setSearchQuery(value);
    setSearchBar(value.length <= 0);
    fetchSystems(value);
  };

  /**
   *
   * Handles change in search value
   * @param {Event} event search text
   *
   */
  const handleSearchOnChange = (event) => {
    const { value } = event.target;
    setSearchQuery(value);
  };

  /**
   *
   * Downloads data in the form of csv file
   *
   */
  const handleDownloadCsv = async () => {
    setLoading(true);
    const queryParams = [
      ['export', true],
      ['exportFields', csvExportFileds.toString()],
      ['type', Constants.filter.SYSTEMS],
    ];
    try {
      const response = await filterPageService.getSystemsList(queryParams);
      setCsvDownloadData(getFormattedCSVData(response.data));
      setLoading(false);
      systemsCsv.current.link.click();
    } catch (e) {
      setLoading(false);
    }
  };

  /**
   *
   * Opens delete modal
   * @param {Object} data data of the selected gigger
   *
   */
  const handleDelete = (deleteData) => () => {
    setIsDeleteModalOpen(true);
    setData(deleteData);
  };

  /**
   *
   * Opens approve modal
   * @param {Object} data data of the selected gigger
   *
   */
  const handleApprove = (approveData) => () => {
    setIsApproveModalOpen(true);
    setData(approveData);
  };

  /**
   *
   * Closes the opened modal
   *
   */
  const handleModalClose = () => {
    setIsApproveModalOpen(false);
    setIsDeleteModalOpen(false);
  };

  /**
   *
   * Handles sorting in column
   * @param {string} name header
   *
   */
  const handleSorting = (name) => {
    if (sortedBy === name) {
      setSortAscending(!sortAscending);
    } else {
      setSortedBy(name);
      setSortAscending(true);
    }
  };

  /**
   *
   * Opens out search bar
   *
   */
  const openSearchBar = () => {
    setSearchBar(false);
  };

  return (
    <>
      <CSVLink data={csvDownloadData} filename="systems.csv" className="d-none" ref={systemsCsv}>
        click
      </CSVLink>
      <FilterListWrapper>
        <ToolBar
          searchbar={searchbar}
          handleSearch={handlesearchSystems}
          handleSearchOnChange={handleSearchOnChange}
          searchQuery={searchQuery}
          handleDownloadCsv={handleDownloadCsv}
          openSearchBar={openSearchBar}
          data={filterData}
        />
        {pageLoading && (
        <FilterListWrapper>
          <LoadingCard />
        </FilterListWrapper>
        )}
        {!pageLoading && (
        <>
          <ListViewHeader
            handleSorting={handleSorting}
            sortedBy={sortedBy}
            sortAscending={sortAscending}
          />
          {filterData.length > 0 ? (
            <>
              <ListView
                data={filterData}
                handleApprove={handleApprove}
                handleDelete={handleDelete}
                type={<FormattedMessage id="common_systems" defaultMessage="Systems" />}
              />
              <a
                href
                data-backdrop="static"
                data-keyboard="false"
                data-toggle="modal"
                data-target="#FilterModal"
                ref={openElement}
              />
              {isApproveModalOpen && (
              <Modal
                approve={isApproveModalOpen}
                id={data._id}
                type={Constants.filter.SYSTEMS}
                ModalClose={handleModalClose}
                handleToast={handleToast}
                fetchData={fetchSystems}
              />
              )}
              {isDeleteModalOpen && (
              <Modal
                remove={isDeleteModalOpen}
                id={data._id}
                type={Constants.filter.SYSTEMS}
                ModalClose={handleModalClose}
                handleToast={handleToast}
                fetchData={fetchSystems}
              />
              )}
            </>
          ) : (
            <>
              <Border />
              <WhiteCard className="my-3">
                {searchQuery.length > 0 ? (
                  <FormattedMessage id="common_no_search_results_found" defaultMessage="No search results found" />
                ) : (
                  <FormattedMessage id="common_no_system_found" defaultMessage="No systems found" />
                )}
              </WhiteCard>
            </>
          )}
        </>
        )}
      </FilterListWrapper>
      {!pageLoading && meta.pages > 1 && (
      <FilterListWrapper style={{ backgroundColor: 'transparent' }}>
        <Pagination cursor={meta} gotoPage={handleGotoPage} />
      </FilterListWrapper>
      )}
    </>
  );
};

export default SystemsTab;
